import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiTrendingUp, HiShieldCheck, HiUsers, HiChevronRight, HiChartBar } from 'react-icons/hi';
import { productsAPI } from '../api';

const FEATURES = [
  {
    Icon: HiTrendingUp,
    color: 'var(--blue-light)',
    bg: 'var(--blue-pale)',
    title: 'Smart investment products',
    desc: 'Choose from a range of plans with clear, predictable daily returns.',
  },
  {
    Icon: HiShieldCheck,
    color: '#1a9e5c',
    bg: 'rgba(26,158,92,0.12)',
    title: 'Secure & transparent',
    desc: 'Every transaction is logged and your earnings tracked in real time.',
  },
  {
    Icon: HiUsers,
    color: 'var(--gold)',
    bg: 'var(--gold-pale)',
    title: 'Earn by referring friends',
    desc: 'Share your referral link and earn a bonus for every person who joins.',
  },
];

const STEPS = [
  {
    n: '1',
    title: 'Create your account',
    desc: 'Sign up in under a minute with your email or Google.',
  },
  {
    n: '2',
    title: 'Pick a product',
    desc: 'Choose an investment plan that fits your goals and budget.',
  },
  {
    n: '3',
    title: 'Watch it grow',
    desc: "Earn daily returns and withdraw whenever you're ready.",
  },
];

/* ── Compact product preview card ──────────────────────────── */
function ProductPreviewCard({ p, navigate }) {
  const price     = parseFloat(p.price)             || 0;
  const dailyRate = parseFloat(p.daily_return_rate) || 0;
  const totalRate = parseFloat(p.total_return_rate) || 0;
  const dailyGHS  = (price * dailyRate / 100).toFixed(2);
  const profitGHS = (price * totalRate / 100).toFixed(2);

  return (
    <div style={{
      background: 'var(--white)',
      borderRadius: 'var(--radius)',
      boxShadow: 'var(--shadow)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Image / placeholder */}
      {p.image_url ? (
        <img
          src={p.image_url}
          alt={p.name}
          onError={e => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
          style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }}
        />
      ) : null}
      <div style={{
        display: p.image_url ? 'none' : 'flex',
        width: '100%', aspectRatio: '16 / 9',
        alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, var(--blue-pale), #dbeafe)',
        color: 'var(--blue-mid)',
      }}>
        <HiChartBar size={36} />
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--gray-800)', lineHeight: 1.3 }}>
          {p.name}
        </div>

        {/* Stats row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          gap: 4,
          background: 'var(--gray-100)',
          borderRadius: 8, padding: '8px 6px',
          textAlign: 'center',
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--blue-mid)' }}>
              GHS {price}
            </div>
            <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 1 }}>Price</div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gray-800)' }}>
              {dailyRate}%
            </div>
            <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 1 }}>Daily</div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gray-800)' }}>
              {p.duration_days}d
            </div>
            <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 1 }}>Duration</div>
          </div>
        </div>

        {/* Profit line */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          paddingTop: 2,
        }}>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Net profit</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--success)' }}>
              + GHS {profitGHS}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700,
              background: '#e6f9ef', color: 'var(--success)',
              borderRadius: 4, padding: '2px 5px',
            }}>
              +{totalRate}%
            </span>
          </div>
        </div>

        {/* CTA button */}
        <button
          onClick={() => navigate('/register')}
          style={{
            marginTop: 4,
            width: '100%',
            background: 'linear-gradient(135deg, var(--blue) 0%, var(--blue-mid) 100%)',
            color: 'white',
            border: 'none',
            borderRadius: 10,
            padding: '10px 0',
            fontSize: 13, fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}
        >
          Invest Now <HiChevronRight size={15} />
        </button>
      </div>
    </div>
  );
}

/* ── Skeleton placeholder while loading ────────────────────── */
function ProductSkeleton() {
  return (
    <div style={{
      background: 'var(--white)',
      borderRadius: 'var(--radius)',
      boxShadow: 'var(--shadow)',
      overflow: 'hidden',
    }}>
      <div style={{
        width: '100%', aspectRatio: '16 / 9',
        background: 'var(--gray-200)',
        animation: 'pulse 1.4s ease-in-out infinite',
      }} />
      <div style={{ padding: '12px 14px 14px' }}>
        <div style={{ height: 14, borderRadius: 6, background: 'var(--gray-200)', marginBottom: 10, width: '70%' }} />
        <div style={{ height: 48, borderRadius: 8, background: 'var(--gray-100)', marginBottom: 8 }} />
        <div style={{ height: 12, borderRadius: 6, background: 'var(--gray-200)', width: '55%', marginBottom: 10 }} />
        <div style={{ height: 36, borderRadius: 10, background: 'var(--gray-200)' }} />
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    productsAPI.list()
      .then(r => setProducts((r.data.products || []).slice(0, 4)))
      .catch(() => {})
      .finally(() => setLoadingProducts(false));
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-100)', fontFamily: 'var(--font-body)' }}>

      {/* ── Hero ──────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(160deg, var(--blue) 0%, var(--blue-mid) 60%, var(--blue-light) 100%)',
        padding: '52px 24px 60px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative blobs */}
        <div style={{
          position: 'absolute', top: -50, right: -50,
          width: 200, height: 200, borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -70, left: -40,
          width: 220, height: 220, borderRadius: '50%',
          background: 'rgba(255,255,255,0.04)',
          pointerEvents: 'none',
        }} />

        {/* Logo mark */}
        <div style={{
          width: 76, height: 76, borderRadius: 22,
          background: 'rgba(255,255,255,0.14)',
          border: '1px solid rgba(255,255,255,0.22)',
          margin: '0 auto 22px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
        }}>
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: 30, fontWeight: 900,
            color: 'white', letterSpacing: -1,
          }}>VG</span>
        </div>

        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 34, fontWeight: 700,
          color: 'white',
          lineHeight: 1.2,
          marginBottom: 14,
          position: 'relative',
        }}>
          Grow your wealth<br />
          <span style={{ color: 'var(--gold-light)' }}>with confidence</span>
        </h1>

        <p style={{
          fontSize: 15,
          color: 'rgba(255,255,255,0.9)',
          lineHeight: 1.65,
          maxWidth: 300,
          margin: '0 auto 34px',
          position: 'relative',
        }}>
          Smart investment products, daily returns, and a platform built for everyone.
        </p>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', position: 'relative' }}>
          <button
            onClick={() => navigate('/register')}
            style={{
              background: 'linear-gradient(135deg, var(--gold) 0%, var(--gold-light) 100%)',
              color: 'var(--blue)',
              border: 'none',
              borderRadius: 12,
              padding: '14px 26px',
              fontSize: 15, fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              boxShadow: '0 4px 18px rgba(201,151,58,0.42)',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            Get started <HiChevronRight size={18} />
          </button>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'rgba(255,255,255,0.13)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.28)',
              borderRadius: 12,
              padding: '14px 26px',
              fontSize: 15, fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
            }}
          >
            Sign in
          </button>
        </div>
      </div>

      {/* ── Features ──────────────────────────────────────── */}
      <div style={{ padding: '30px 16px 8px' }}>
        <p style={{
          fontSize: 11, fontWeight: 700, letterSpacing: 1.4,
          color: 'var(--gray-600)', textTransform: 'uppercase',
          textAlign: 'center', marginBottom: 16,
        }}>
          Why Vest-Grow
        </p>

        {FEATURES.map(({ Icon, color, bg, title, desc }) => (
          <div key={title} style={{
            background: 'var(--white)',
            borderRadius: 'var(--radius)',
            padding: '16px 18px',
            marginBottom: 12,
            boxShadow: 'var(--shadow)',
            display: 'flex', gap: 16, alignItems: 'flex-start',
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: bg, color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon size={22} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--gray-800)', marginBottom: 4 }}>
                {title}
              </div>
              <div style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.6 }}>
                {desc}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Product preview ───────────────────────────────── */}
      <div style={{ padding: '20px 16px 8px' }}>
        {/* Section header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16,
        }}>
          <p style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 1.4,
            color: 'var(--gray-600)', textTransform: 'uppercase',
            margin: 0,
          }}>
            Investment Plans
          </p>
          <button
            onClick={() => navigate('/register')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 2,
              fontSize: 12, fontWeight: 600, color: 'var(--blue-mid)',
              fontFamily: 'var(--font-body)', padding: 0,
            }}
          >
            View all <HiChevronRight size={15} />
          </button>
        </div>

        {/* 2-column grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
        }}>
          {loadingProducts
            ? Array.from({ length: 4 }).map((_, i) => <ProductSkeleton key={i} />)
            : products.length > 0
              ? products.map(p => <ProductPreviewCard key={p.id} p={p} navigate={navigate} />)
              : (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center', padding: '28px 0',
                  color: 'var(--gray-400)', fontSize: 13,
                }}>
                  Products coming soon.
                </div>
              )
          }
        </div>

        {/* Sign-up nudge below the grid */}
        {!loadingProducts && products.length > 0 && (
          <p style={{
            textAlign: 'center', marginTop: 14,
            fontSize: 12, color: 'var(--gray-500)', lineHeight: 1.5,
          }}>
            Create a free account to browse all plans and start investing.
          </p>
        )}
      </div>

      {/* ── How it works ──────────────────────────────────── */}
      <div style={{ padding: '20px 16px 8px' }}>
        <p style={{
          fontSize: 11, fontWeight: 700, letterSpacing: 1.4,
          color: 'var(--gray-600)', textTransform: 'uppercase',
          textAlign: 'center', marginBottom: 16,
        }}>
          How it works
        </p>

        <div style={{
          background: 'var(--white)',
          borderRadius: 'var(--radius)',
          padding: '4px 18px',
          boxShadow: 'var(--shadow)',
        }}>
          {STEPS.map(({ n, title, desc }, i) => (
            <div key={n} style={{
              display: 'flex', gap: 16, alignItems: 'flex-start',
              padding: '18px 0',
              borderBottom: i < STEPS.length - 1 ? '1px solid var(--gray-200)' : 'none',
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, var(--blue) 0%, var(--blue-mid) 100%)',
                color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700,
              }}>
                {n}
              </div>
              <div style={{ paddingTop: 4 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--gray-800)', marginBottom: 4 }}>
                  {title}
                </div>
                <div style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.6 }}>
                  {desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom CTA ────────────────────────────────────── */}
      <div style={{ padding: '28px 16px 48px' }}>
        <div style={{
          background: 'linear-gradient(135deg, var(--blue) 0%, var(--blue-mid) 100%)',
          borderRadius: 'var(--radius)',
          padding: '28px 22px',
          boxShadow: 'var(--shadow-lg)',
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: 'var(--font-display)',
            fontSize: 21, fontWeight: 700,
            color: 'white', marginBottom: 8,
          }}>
            Ready to start growing?
          </div>
          <p style={{
            fontSize: 13, color: 'rgba(255,255,255,0.88)',
            lineHeight: 1.6, marginBottom: 22,
          }}>
            Join thousands of investors building their future with Vest-Grow.
          </p>

          <button
            onClick={() => navigate('/register')}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, var(--gold) 0%, var(--gold-light) 100%)',
              color: 'var(--blue)',
              border: 'none',
              borderRadius: 12,
              padding: '15px',
              fontSize: 15, fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              marginBottom: 16,
              boxShadow: '0 4px 14px rgba(201,151,58,0.35)',
            }}
          >
            Create a free account
          </button>

          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none', border: 'none',
              color: 'rgba(255,255,255,0.85)',
              fontSize: 14, cursor: 'pointer',
              fontFamily: 'var(--font-body)',
            }}
          >
            Already have an account?{' '}
            <span style={{ color: 'white', fontWeight: 600 }}>Sign in</span>
          </button>
        </div>
      </div>

    </div>
  );
}